import { useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────
// CSC Proposal math — originally mirrored from the 181.76kwp.xlsx Excel
// (Input / Generation / Breakeven / Environmental sheets), but PEAK_SUN_HOURS
// was bumped from 3.3 → 3.4 on 2026-05-21 to match the Bill tab + System Size
// calculator (useCscBillProposal.YIELD_PER_KWP, commercialSolarCalculator).
// One source of truth: every CSC tab now reports the same kWh/RM numbers.
//
// Sample outputs (181.76 kWp · LV tariff 0.515 · RM 1800/kWp · O&M RM 12,478):
//   Year-1 gen    = kwp × 3.4 × 365 = 225,562.16 kWh
//   Year-N gen    = yr1 × (0.98 − 0.007 × (N−1)) / 0.98
//   Year-2 CA tax = cost × 0.34 × 0.24                (claim starts year after install)
// IRR/savings/payback/LCOE all scale ~3% higher than the original Excel
// reference because of the +0.1 PSH bump.
// ─────────────────────────────────────────────────────────────────────────

// ── Generation constants (Input sheet) ──
export const PEAK_SUN_HOURS = 3.4;            // Aligned to Bill tab + Calc (was 3.3 / Input!D11)
const INITIAL_DERATE_YEAR_1 = 0.98;            // Generation!C9 — 2% Y1 loss
const ANNUAL_DEGRADATION = 0.007;              // Generation!B10:B28 — 0.7%/yr linear
export const PROJECTION_YEARS = 20;            // Generation rows 9-28

// ── Environmental constants (Input!J30:J31) ──
const CO2_TONNES_PER_MWH = 0.55;               // CO2 = 0.55 × annualGen / 1000
const TREES_PER_TONNE_CO2 = 1000 / 87;          // ≈ 11.49 trees per tonne

// ── Tax (Breakeven sheet) ──
const CORPORATE_TAX_RATE = 0.24;               // J2 = 0.24

// Capital Allowance schedule — fully absorbs Qualifying Expenditure over 6 years.
// Excel cells N20-N25 / O20-O25 / P20-P25 (rates per year of assessment):
//   IA 20% + AA 14% = 34% (initial claim year)
//   AA 14% each (4 follow-up years)
//   AA 10% (final year, balances to 100%)
// Note: claim does NOT start in the installation year. The customer can only
// begin claiming from the year AFTER installation (Y2 in our projection).
// Y1 in the table = installation year = no CA/GITA claim.
const CA_SCHEDULE = [0.34, 0.14, 0.14, 0.14, 0.14, 0.10];
const CA_FIRST_CLAIM_YEAR = 2;     // Y1 = installation, Y2 = first claim

// ── Defaults ──
export const DEFAULT_PRICE_PER_KWP = 1800;     // Excel Input!I8 = D9 × 1800
export const DEFAULT_OM_PER_KWP_PER_YEAR = 70; // ≈ Excel Input!F1 / kWp (12478 / 181.76 ≈ 68.7)

// O&M tier table — mirrors the CSC calculator's MAINT_TIERS so the proposal's
// default O&M matches what the salesperson sees on the calculator screen.
// Tier ceilings (kWp) and the flat RM/year rate for systems up to that ceiling.
const MAINT_TIERS = [
    { max: 6.82, rate: 1800 }, { max: 10.54, rate: 2300 }, { max: 20, rate: 2600 },
    { max: 40,   rate: 3500 }, { max: 60,    rate: 4500 }, { max: 80, rate: 4500 },
    { max: 100,  rate: 4500 }, { max: 300,   rate: 5000 }, { max: 600, rate: 7500 },
    { max: 1000, rate: 11000 },
];
export function getMaintenanceForKwp(kwp) {
    const k = Number(kwp) || 0;
    for (const t of MAINT_TIERS) if (k <= t.max) return t.rate;
    return 11000; // > 1000 kWp falls back to top tier
}
const DEFAULT_LOAN_RATE = 0.05;                // 5% p.a. (matches Astern bank-loan Excel)
const DEFAULT_LOAN_TERM_MONTHS = 84;           // 7 years × 12 = 84 months
const DEFAULT_SARE_TARIFF_DISCOUNT = 0.95;     // legacy fallback (only used if kWp = 0)

// ── SARE tariff brackets by system size (RM/kWh) ──
// Authoritative price list provided by Astern (May 2026). Smaller systems pay
// more per kWh because absolute monthly revenue is lower; >600 kWp earns the
// best (lowest) tariff.
export function defaultSareTariffByKwp(kwp) {
    const n = Number(kwp) || 0;
    if (n < 40) return 0;        // SARE not offered for <40 kWp systems
    if (n <= 150) return 0.36;   // 40-150 kWp
    if (n <= 300) return 0.32;   // 151-300 kWp
    if (n <= 600) return 0.30;   // 301-600 kWp
    return 0.28;                  // > 600 kWp
}

// CSC tariff list (kept in sync with the Bill tab's TARIFFS in cscBillTab.jsx)
export const CSC_PROPOSAL_TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

// ─────────────────────────────────────────────────────────────────────────
// Pure formula helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Year-N annual generation in kWh.
 *   Year 1: kwp × 3.4 × 365  (PSH aligned to Bill tab + Calc, 2026-05-21)
 *   Year N>1: yr1 × (0.98 − 0.007 × (N−1)) / 0.98
 */
export function annualGeneration(kwp, year) {
    const yr1 = kwp * PEAK_SUN_HOURS * 365;
    if (year <= 1) return yr1;
    const cumEff = INITIAL_DERATE_YEAR_1 - ANNUAL_DEGRADATION * (year - 1);
    return (yr1 * cumEff) / INITIAL_DERATE_YEAR_1;
}

/**
 * Capital Allowance claimable in year N (1-indexed). Returns 0 after Year 6.
 * CA × corporate tax rate = the tax saving deducted from cashflow.
 */
export function caForYear(systemCost, year) {
    // Year of installation (year 1) has no allowance — claim begins the year after.
    const idx = year - CA_FIRST_CLAIM_YEAR;
    if (idx < 0) return 0;
    const rate = CA_SCHEDULE[idx] ?? 0;
    return systemCost * rate;
}

/** PMT — annuity payment (Excel PMT function). */
export function pmt(monthlyRate, nMonths, principal) {
    if (!principal || nMonths <= 0) return 0;
    if (monthlyRate === 0) return principal / nMonths;
    return (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -nMonths));
}

/**
 * Internal Rate of Return — Newton's method, 100 iterations.
 * Returns the periodic rate where NPV(cashflows) = 0. cashflows[0] is t=0
 * (typically the negative investment), cashflows[N] is end of year N.
 */
export function irr(cashflows, guess = 0.1) {
    if (!cashflows || cashflows.length < 2) return null;
    const npv = (r) =>
        cashflows.reduce((sum, cf, i) => sum + cf / Math.pow(1 + r, i), 0);
    const dNpv = (r) =>
        cashflows.reduce(
            (sum, cf, i) => sum - (i * cf) / Math.pow(1 + r, i + 1),
            0
        );
    let r = guess;
    for (let i = 0; i < 100; i++) {
        const f = npv(r);
        if (Math.abs(f) < 1e-6) return r;
        const df = dNpv(r);
        if (df === 0) break;
        const rNew = r - f / df;
        if (!Number.isFinite(rNew) || rNew < -0.99) return null;
        r = rNew;
    }
    return Number.isFinite(r) ? r : null;
}

// ─────────────────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────────────────

/**
 * useCscProposalMath — given the user's CSC proposal inputs, produce the
 * full 20-year projection, headline KPIs, environmental impact, and
 * financing scenarios needed by the proposal template.
 *
 * Inputs (all optional except kwp, systemCost, tariffRate):
 *   kwp              system size kWp
 *   systemCost       capex RM (defaults to kwp × DEFAULT_PRICE_PER_KWP if blank)
 *   tariffRate       RM/kWh (one of CSC_PROPOSAL_TARIFFS rates)
 *   omAnnual         O&M RM/yr (defaults to kwp × DEFAULT_OM_PER_KWP_PER_YEAR)
 *   monthlyBill      RM/month (echoed back for display; not used in math today)
 *   includeCA        bool — apply Capital Allowance tax saving (default true)
 *   includeITA       bool — apply Investment Tax Allowance (default true; uses
 *                    a simplified one-shot Y1 60% claim — full ITA needs the
 *                    customer's taxable income which isn't on this form)
 *   includeOutright  bool — include Outright Purchase scenario in the output
 *   includeSARE      bool — include SARE (Solar Lease) scenario
 *   includeLoan      bool — include Loan-financed Outright scenario
 *   sareTariffRate   RM/kWh — what SARE provider charges customer (default = tariffRate × 0.95)
 *   loanPrincipal    RM — defaults to systemCost
 *   loanRate         annual rate (default 0.07)
 *   loanTermMonths   default 120
 */
export default function useCscProposalMath(inputs) {
    return useMemo(() => {
        const {
            kwp = 0,
            systemCost: systemCostRaw,
            tariffRate = 0,
            omAnnual: omAnnualRaw,
            monthlyBill = 0,
            includeCA = true,
            includeITA = true,
            includeOutright = true,
            includeSARE = false,
            includeLoan = false,
            sareTariffRate: sareTariffRateRaw,
            loanPrincipal: loanPrincipalRaw,
            loanRate = DEFAULT_LOAN_RATE,
            loanTermMonths = DEFAULT_LOAN_TERM_MONTHS,
        } = inputs || {};

        if (!kwp || !tariffRate) {
            return { invalid: true, reason: 'missing kwp or tariffRate' };
        }

        const systemCost = systemCostRaw && systemCostRaw > 0
            ? systemCostRaw
            : kwp * DEFAULT_PRICE_PER_KWP;
        const omAnnual = omAnnualRaw != null && omAnnualRaw >= 0
            ? omAnnualRaw
            : getMaintenanceForKwp(kwp); // tier-based default (mirrors the CSC calculator)
        const sareTariff = sareTariffRateRaw && sareTariffRateRaw > 0
            ? sareTariffRateRaw
            : (defaultSareTariffByKwp(kwp) || tariffRate * DEFAULT_SARE_TARIFF_DISCOUNT);
        const loanPrincipal = loanPrincipalRaw && loanPrincipalRaw > 0
            ? loanPrincipalRaw
            : systemCost;

        // Loan repayment — PMT amortization (reducing balance) to match the
        // Astern bank-loan Excel: PMT(rate/12, months, principal).
        //   Yearly installment = monthly PMT × 12
        //   Total repayment = monthly PMT × months
        //   Total interest = total repayment − principal
        // Spot-check 172,800 @ 5% × 7yr → 2,442/mo · 29,306/yr · 205,156 total ✓
        const loanYears = loanTermMonths / 12;
        const loanMonthlyRate = loanRate / 12;
        const loanMonthlyPmt = includeLoan
            ? pmt(loanMonthlyRate, loanTermMonths, loanPrincipal)
            : 0;
        const loanAnnualPmt = loanMonthlyPmt * 12;
        const loanTotalRepayment = loanMonthlyPmt * loanTermMonths;
        const loanTotalInterest = loanTotalRepayment - (includeLoan ? loanPrincipal : 0);

        // ITA — Excel scenario tied to customer's taxable income (J1).
        // Without that data we apply a simplified "claim 60% of system cost
        // against Y1 income tax" assumption. ITA gives a one-shot tax credit
        // (cost × 0.60 × 0.24) realized in Year 1.
        const itaCredit = includeITA ? systemCost * 0.60 * CORPORATE_TAX_RATE : 0;

        // ── 20-year projection ──
        const years = [];
        let cumGen = 0;
        let cumOutright = -systemCost;
        let cumLoan = 0;                    // financed — no upfront cash out
        let cumSARE = 0;                    // SARE provider owns system — no upfront

        const outrightCashflows = [-systemCost];
        const loanCashflows = [0];
        const sareCashflows = [0];

        for (let y = 1; y <= PROJECTION_YEARS; y++) {
            const gen = annualGeneration(kwp, y);
            cumGen += gen;

            // Annual gross revenue from solar
            const grossRevenue = gen * tariffRate;
            const netSavings = grossRevenue - omAnnual;     // kept for any downstream UI that wants net-of-O&M

            // Capital Allowance tax saving (claim starts year AFTER install).
            const caClaim = includeCA ? caForYear(systemCost, y) : 0;
            const caTaxSaving = caClaim * CORPORATE_TAX_RATE;

            // GITA one-shot credit lands in Y2 (claim begins the year after install).
            const itaTaxSaving = (y === CA_FIRST_CLAIM_YEAR) ? itaCredit : 0;

            // Outright cashflow — matches Excel Breakeven: gross savings + tax
            // incentives, no O&M deduction. O&M is shown to the customer as a
            // separate line in the proposal but isn't netted from Net Return.
            const outrightCF = grossRevenue + caTaxSaving + itaTaxSaving;
            cumOutright += outrightCF;
            outrightCashflows.push(outrightCF);

            // Loan cashflow — same gross savings + tax savings, minus loan payment
            // during the loan term.
            const loanPayThisYear = y <= loanYears ? loanAnnualPmt : 0;
            const loanCF = grossRevenue + caTaxSaving + itaTaxSaving - loanPayThisYear;
            cumLoan += loanCF;
            loanCashflows.push(loanCF);

            // SARE — TNB tariff escalates 8% every 3 years (per SPPA Excel sheet),
            // SARE rate stays flat at the contract value. So the savings GAP
            // widens over time and yearly savings grow despite panel degradation.
            //   Tariff_year = base × 1.08^floor((year-1)/3)
            const tariffYear = tariffRate * Math.pow(1.08, Math.floor((y - 1) / 3));
            const sareRevenue = gen * (tariffYear - sareTariff);
            const sareCF = sareRevenue;
            cumSARE += sareCF;
            sareCashflows.push(sareCF);

            years.push({
                year: y,
                generation: gen,
                cumGeneration: cumGen,
                grossRevenue,
                omCost: omAnnual,
                netSavings,
                caClaim,
                caTaxSaving,
                itaTaxSaving,
                outright: {
                    annualCF: outrightCF,
                    cumulative: cumOutright,
                },
                loan: {
                    annualCF: loanCF,
                    annualPayment: loanPayThisYear,
                    cumulative: cumLoan,
                },
                tariffYear,
                sare: {
                    annualCF: sareCF,
                    sareCost: gen * sareTariff,
                    cumulative: cumSARE,
                    tariffYear,
                    discount: tariffYear - sareTariff,
                },
            });
        }

        // ── Headline KPIs ──

        // Breakeven year = first year cumulative cashflow turns positive (INTEGER)
        const outrightBreakeven = years.find((y) => y.outright.cumulative >= 0)?.year ?? null;
        const loanBreakeven = years.find((y) => y.loan.cumulative >= 0)?.year ?? null;

        // Exact fractional payback: linear-interpolate WITHIN the breakeven year.
        // If cumulative crosses 0 during year N, payback = (N-1) + |cum_{N-1}|/(cum_N - cum_{N-1}).
        function exactPaybackFrom(startCum, annualCFs) {
            let prev = startCum;
            for (let i = 0; i < annualCFs.length; i++) {
                const cur = prev + annualCFs[i];
                if (cur >= 0) {
                    const fraction = annualCFs[i] > 0 ? -prev / annualCFs[i] : 0;
                    return i + fraction; // years from time 0
                }
                prev = cur;
            }
            return null;
        }

        // Per-year cashflows (excluding the initial -systemCost outlay)
        const cfOutrightWith = years.map((y) => y.outright.annualCF);                    // grossRevenue + CA + GITA
        const cfOutrightNoIncentives = years.map((y) => y.grossRevenue);                 // pure solar revenue, no CA, no GITA
        const cfLoanWith = years.map((y) => y.loan.annualCF);                            // grossRevenue + CA + GITA − loan payment
        const cfLoanNoIncentives = years.map((y) => y.grossRevenue - (y.loan.annualPayment || 0));  // no CA, no GITA

        const outrightBreakevenExact = exactPaybackFrom(-systemCost, cfOutrightWith);
        const outrightBreakevenExactNoIncentives = exactPaybackFrom(-systemCost, cfOutrightNoIncentives);
        // Loan: zero capex outlay (financed by bank), cumulative starts at 0
        const loanBreakevenExact = exactPaybackFrom(0, cfLoanWith);
        const loanBreakevenExactNoIncentives = exactPaybackFrom(0, cfLoanNoIncentives);

        // IRR
        const outrightIRR = irr(outrightCashflows);
        const outrightIRRWithoutCA = irr([
            -systemCost,
            ...years.map((y) => y.grossRevenue - y.omCost),
        ]);

        // 10-year totals (first 10 projection years, degradation-aware) — used by the
        // WhatsApp/breakdown so its 10-yr figure matches the proposal's model.
        const total10yrOutright = years.slice(0, 10).reduce((s, y) => s + y.outright.annualCF, 0);
        // 20-year totals
        const total20yrOutright = years.reduce((s, y) => s + y.outright.annualCF, 0);
        const total20yrLoan = years.reduce((s, y) => s + y.loan.annualCF, 0);
        const total20yrSARE = years.reduce((s, y) => s + y.sare.annualCF, 0);
        const totalGen20yr = cumGen;

        // Average annual
        const avgAnnualOutright = total20yrOutright / PROJECTION_YEARS;
        const avgAnnualLoan = total20yrLoan / PROJECTION_YEARS;
        const avgAnnualSARE = total20yrSARE / PROJECTION_YEARS;
        const avgAnnualGen = totalGen20yr / PROJECTION_YEARS;

        // LCOE — system cost ÷ total 20-yr generation (no discounting)
        const lcoe = systemCost / totalGen20yr;

        // Year-1 highlights
        const yr1Gen = years[0].generation;
        const yr1MonthlyGen = yr1Gen / 12;
        const yr1MonthlyRevenue = years[0].grossRevenue / 12;
        const yr1MonthlySaving = years[0].netSavings / 12;
        const yr1AnnualSaving = years[0].netSavings;

        // Environmental (computed from Year-1 generation; Excel does the same)
        const co2TonnesPerYear = (CO2_TONNES_PER_MWH * yr1Gen) / 1000;
        const treesSavedPerYear = co2TonnesPerYear * TREES_PER_TONNE_CO2;
        const co2TonnesOver20yr = (CO2_TONNES_PER_MWH * totalGen20yr) / 1000;

        // Reduction percentages
        const yr1BillReductionPct = monthlyBill > 0
            ? Math.min(1, yr1MonthlyRevenue / monthlyBill)
            : null;

        // CA / ITA totals
        const cumCAOverYears = years.reduce((s, y) => s + y.caClaim, 0);
        const cumCATaxSaving = years.reduce((s, y) => s + y.caTaxSaving, 0);

        return {
            invalid: false,

            // Echoed inputs
            kwp,
            systemCost,
            tariffRate,
            omAnnual,
            monthlyBill,
            includeCA,
            includeITA,
            includeOutright,
            includeSARE,
            includeLoan,
            sareTariff,
            loanPrincipal,
            loanRate,
            loanTermMonths,
            loanMonthlyPmt,
            loanAnnualPmt,
            itaCredit,

            // Year-1 headlines
            yr1Generation: yr1Gen,
            yr1MonthlyGen,
            yr1MonthlyRevenue,
            yr1MonthlySaving,
            yr1AnnualSaving,
            yr1BillReductionPct,

            // 10/20-year totals
            totalGen20yr,
            total10yrOutright,
            total20yrOutright,
            total20yrLoan,
            total20yrSARE,
            avgAnnualOutright,
            avgAnnualLoan,
            avgAnnualSARE,
            avgAnnualGen,

            // Breakeven + ROI
            outrightBreakeven,
            loanBreakeven,
            outrightBreakevenExact,
            outrightBreakevenExactNoIncentives,
            loanBreakevenExact,
            loanBreakevenExactNoIncentives,
            outrightIRR,
            outrightIRRWithoutCA,
            lcoe,
            cumCAOverYears,
            cumCATaxSaving,

            // Environmental
            co2TonnesPerYear,
            treesSavedPerYear,
            co2TonnesOver20yr,

            // Full projection table
            years,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(inputs)]);
}
